import { graphql } from "gatsby";
import {
  ListBlock,
  ProductGroup,
  NestedTagFilterer,
  StickyFilter,
  ECommerceHelper,
  JetshopHelper,
  ListTransformerProps,
} from "@parksandresorts/core";
import SiteConfig from "components/stores/site-configuration-store";
import { useCallback, useMemo } from "react";

function isDefined<T>(val: T | undefined | null): val is T {
  return val !== undefined && val !== null;
}

// This type isn't exported and its name collides with another type, so we need to to this
type Product = ProductGroup["listObjects"][0];
type CustomProductGroup = ProductGroup<{ tags: { id: string; title: string }[] }>;
const coerceTheme = (theme: string | null) => (theme ?? undefined) as Product["componentTheme"];

const mapTags = (tags: Queries.ProductListBlockFragment["filterTags"]) =>
  tags
    ?.filter(isDefined)
    .filter(({ title }) => !!title)
    .map((t) => ({
      id: t.id,
      // Type coercion can be done safely thanks to the filter above
      title: t.title as string,
    })) ?? [];

export default function ProductListBlock({
  productLists: originalProductList,
  filterHeadline,
  filterTags,
  ...props
}: Queries.ProductListBlockFragment) {
  const currency = ECommerceHelper.getJetshopConfig().currencyDisplayName;

  const hasFilterTags = (filterTags?.length ?? 0) > 0;

  const mappedProductGroups: CustomProductGroup[] = useMemo(
    () =>
      (originalProductList ?? []).filter(isDefined).map((list) => {
        const objects: CustomProductGroup["listObjects"] = [];

        list.listObjects?.forEach((product) => {
          if (!product) return;

          if (product.__typename !== "ContentfulProductBlock") return;

          let prices;

          if (product.hidePriceInProductListBlock) {
            prices = undefined;
          } else if (product.jetshopProduct?.price || product.customPriceText) {
            prices = {
              type: "description" as const,
              price: product.customPriceText ?? `${product.jetshopProduct?.price} ${currency}`,
            };
          } else {
            prices = undefined;
          }

          objects.push({
            id: product.id,
            heading: product.productName ?? "",
            prefix: product.label ?? undefined,
            buttonLabel:
              product.optionalLinkLabelText ?? SiteConfig.getLabel("PRODUCT_CARD_BUY_BUTTON_LABEL", "Köp nu"),
            buttonAction:
              !JetshopHelper.isDateSoldOut([product], true) && product.productPageLink?.slug
                ? product.productPageLink.slug
                : undefined,
            paragraph: product.shortDescription ?? "",
            image: product.productImage?.gatsbyImageData ?? undefined,
            imageUrl: product.productImage?.file?.url ?? undefined,
            fullWidthImage: false,
            productsPrices: prices,
            tags: mapTags(product.tags),
            componentTheme: coerceTheme(product.componentTheme),
          });
        });

        return {
          headline: list.headline ?? "",
          paragraph: list.ingress ?? undefined,
          id: list.id ?? "unknown",
          isExpanded: list.isExpanded ?? true,
          align: (list.alignProducts?.toLocaleLowerCase() as "center" | "start") ?? undefined,
          listObjects: objects,
        };
      }),
    [currency, originalProductList],
  );

  const renderFilter = useCallback(
    (props: ListTransformerProps<CustomProductGroup>) => (
      <StickyFilter
        child={(ref, isStickied) => (
          <NestedTagFilterer
            scrollRef={ref}
            noFilterSelectedLabel={hasFilterTags ? SiteConfig.getLabel("FILTER_SHOW_ALL", "Visa alla") : undefined}
            isStickied={isStickied}
            filterMode="single"
            filterTags={mapTags(filterTags)}
            header={filterHeadline ?? undefined}
            {...props}
          />
        )}
      />
    ),
    [filterHeadline, filterTags, hasFilterTags],
  );

  return (
    <section data-contentful-id={props.contentful_id}>
      <ListBlock renderer="ProductGroups" data={mappedProductGroups} filter={renderFilter} />
    </section>
  );
}

export const fragmentProductListBlock = graphql`
  fragment ProductListBlock on ContentfulProductListBlock {
    contentful_id
    id
    filterHeadline
    filterTags {
      id
      title
    }
    productLists {
      id
      headline
      ingress
      alignProducts
      isExpanded
      listObjects {
        __typename
        ...ProductBlockWithJetshopProduct
      }
    }
  }
`;
